import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { RecaudacionReversarReadDto } from '../models/mutuoRecaudar';
import InputRut from '../../../components/inputs/yup/materialui/inputRut';
import InputSwitch from '../../../components/inputs/yup/materialui/inputSwitch';

import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import CloseIcon from '@mui/icons-material/Close';

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Grid2 from "@mui/material/Grid2";
import DialogActions from "@mui/material/DialogActions";
import { useToast } from '../../../hooks/useToast';
import InputText from '../../../components/inputs/yup/materialui/inputText';
import MButton from '../../../components/inputs/yup/materialui/mButton';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import { Typography } from '@mui/material';
import { SiNo, FormaPago } from '../../../utils/enums';
import { useRecaudacionIndividual } from '../hooks/useRecaudacionIndividual';
import { Recaudador } from '../models/recaudador';
import InputSelect from '../../../components/inputs/yup/materialui/inputSelect';
import { selectorTransversal } from '../../../dtos/selector';
import ConfirmationModal from '../../../components/ui/materialui/confirmationModal';

interface IProps {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    onHide: () => void;
    mutuoRecaudacion: RecaudacionReversarReadDto | null;
    onReversarRecaudacionSuccess?: () => void;
}

interface DeshacerRecaudacionForm  {
    mutuoId: number;
    numeroDividendo: number;
    emisor?: string;
    codigoMutuo?: string;
    omd?: string;
    numeroOperacion?: string;
    esAdministrado?: boolean;
    rutDeudor?: string;
    nombreDeudor?: string;
    procedenciaFondos?: string;
    sucursalPago?: string;
    fechaPago?: Date;
    fechaVencimiento?: Date;
    montoDividendo?: number;
    interesMora?: number;
    gastosCobranza?: number;
    totalRecaudado?: number;
    formaPago?: string;
    estadodividendo?: string;
    fuerecaudadoporinversionista: boolean;
    fechaRemesa: Date | null;
}

const ModalDeshacerRecaudacion: React.FC<IProps> = (props) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [recaudadores, setRecaudadores] = useState<Recaudador[]>([]);
    const [formasPago, setFormasPago] = useState<selectorTransversal[]>([]);
    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {}
    });
    const { addToast } = useToast();
    const { obtenerRecaudadores, obtenerFormasPago, deshacerRecaudacionDividendo, contablizarOperacion } = useRecaudacionIndividual();

    const schemaRecaudacionForm = Yup.object<DeshacerRecaudacionForm>().shape({
        mutuoId: Yup.number().required(),
        numeroDividendo: Yup.number().required(),
        emisor: Yup.string().optional(),
        codigoMutuo: Yup.string().optional(),
        omd: Yup.string().optional(),
        numeroOperacion: Yup.string().optional(),
        esAdministrado: Yup.boolean().optional(),
        rutDeudor: Yup.string().optional(),
        nombreDeudor: Yup.string().optional(),
        procedenciaFondos: Yup.string().optional(),
        sucursalPago: Yup.string().optional(),
        fechaPago: Yup.date().optional(),
        fechaVencimiento: Yup.date().optional(),
        montoDividendo: Yup.number().optional(),
        interesMora: Yup.number().optional(),
        gastosCobranza: Yup.number().optional(),
        totalRecaudado: Yup.number().optional(),
        formaPago: Yup.string().optional(),
        estadodividendo: Yup.string().optional(),
        fuerecaudadoporinversionista: Yup.boolean().required(),
        fechaRemesa: Yup.date().nullable().required()
    });

    const {
        formState: { errors },
        control,
        watch,
        reset,
    } = useForm<DeshacerRecaudacionForm>({
        mode: "onSubmit",
        resolver: yupResolver<DeshacerRecaudacionForm>(schemaRecaudacionForm)
    });

    useEffect(() => {
        const cargarRecaudadores = async () => {
            const data = await obtenerRecaudadores();
            if (data) {
                setRecaudadores(data);
            }
        };
        cargarRecaudadores();
    }, []);

    useEffect(() => {
        const cargarFormasPago = async () => {
            const data = await obtenerFormasPago();
            if (data) {
                setFormasPago(data);
            }
        };
        cargarFormasPago();
    }, []);

    React.useEffect(() => {
        if (props.mutuoRecaudacion) {
            reset({
                ...watch(),
                mutuoId: props.mutuoRecaudacion.id,
                numeroDividendo: props.mutuoRecaudacion.numerodividendo,                
                emisor: props.mutuoRecaudacion.nombreEmisor,
                codigoMutuo: props.mutuoRecaudacion.codigomutuo || '',
                omd: props.mutuoRecaudacion.omd || '',
                numeroOperacion: props.mutuoRecaudacion.numerooperacion,
                esAdministrado: props.mutuoRecaudacion.esadministrado === SiNo.SI,
                rutDeudor: props.mutuoRecaudacion.rut,
                nombreDeudor: props.mutuoRecaudacion.nombre + ' ' + (props.mutuoRecaudacion.apellidopaternodeudor || '') + ' ' + (props.mutuoRecaudacion.apellidomaternodeudor || ''),
                procedenciaFondos: props.mutuoRecaudacion.procedenciadefondos,
                sucursalPago: props.mutuoRecaudacion.sucursalpago || '',
                fechaPago: props.mutuoRecaudacion.fechapago,
                fechaVencimiento: props.mutuoRecaudacion.fechavencimiento, 
                montoDividendo: props.mutuoRecaudacion.montodividendo, 
                interesMora: props.mutuoRecaudacion.interespenal,
                gastosCobranza: props.mutuoRecaudacion.gastoscobranza,
                totalRecaudado: props.mutuoRecaudacion.totalRecaudado,
                formaPago: FormaPago.Documento,
                estadodividendo: props.mutuoRecaudacion.estadodividendo,
                fuerecaudadoporinversionista: props.mutuoRecaudacion.fuerecaudadoporinversionista,
                fechaRemesa: props.mutuoRecaudacion.fecharemesa
            });
        }
    }, [props.mutuoRecaudacion]);

    const handleAceptar = () => {
        setModalConfirmacion({
            show: true,
            title: 'Confirmar reversión',
            message: obtenerMensajeConfirmacion(watch().fechaRemesa, watch().fuerecaudadoporinversionista),
            metodo: async () => {
                setIsSubmitting(true);
                try {
                    const data = watch();
                    
                    // Revesar recaudación de dividendo
                    await deshacerRecaudacionDividendo(data.mutuoId, data.numeroDividendo);

                    // Feedback de recaudación exitosa
                    addToast({
                        mostrar: true,
                        titulo: 'Confirmación',
                        tipo: 'Success',
                        mensaje: 'La reversión de la recaudación se ha completado correctamente. El dividendo ha sido restaurado a su estado anterior.'
                    }); 

                    setIsSubmitting(false);
                    props.onHide();
                    
                    if (props.onReversarRecaudacionSuccess) {
                        props.onReversarRecaudacionSuccess();
                    }

                }
                catch(error){
                    console.error('Error en la reversión de la recaudación:', error);
                    setIsSubmitting(false);
                }
            }
        });
    };

    function customOnHide() {
        props.onHide()
    }

    return (
        <> 
            <ConfirmationModal
                onHide={() => setModalConfirmacion({ ...modalConfirmacion, show: false })}
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                title={modalConfirmacion.title}
                message={modalConfirmacion.message}
            />

            <Dialog open={props.show} onClose={customOnHide} maxWidth="lg">
                <DialogTitle>Reversar recaudación de dividendo</DialogTitle>
                <DialogContent>
                    <form>
                        {/* ——— Grupo 1: Datos del mutuo ——— */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                            Datos del mutuo
                        </Typography>
                        <Grid2 container spacing={2}>
                            {/** Cada campo ocupa 12 cols en xs, 6 en sm, 4 en md, 3 en lg */}
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>        
                                <InputText label="Emisor" name="emisor" control={control} error={errors.emisor} readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputText label="Código mutuo" name="codigoMutuo" control={control} error={errors.codigoMutuo} readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputText label="OMD" name="omd" control={control} error={errors.omd} readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputText label="Número operación" name="numeroOperacion" control={control} error={errors.numeroOperacion} readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputRut 
                                    label="RUT deudor" 
                                    name="rutDeudor" 
                                    control={control} 
                                    error={errors.rutDeudor} 
                                    readonly 
                                />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputText label="Nombre deudor" name="nombreDeudor" control={control} error={errors.nombreDeudor} readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputText label="Último dividendo recaudado" name="numeroDividendo" control={control} error={errors.numeroDividendo} readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4, lg:3}}>   
                                <InputSwitch 
                                    label="Es administrado" 
                                    name="esAdministrado" 
                                    control={control} 
                                    error={errors.esAdministrado}
                                    disabled
                                />
                            </Grid2>
                        </Grid2>

                        {/* ——— Grupo 2: Datos del dividendo/recaudación a reversar  ——— */}
                        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                            Datos de la recaudación
                        </Typography>
                        <Grid2 container spacing={2}>
                            <Grid2 size={{xs:12, sm:6, md:4}}>           
                                <InputSelect 
                                    label="Procedencia fondos" 
                                    name="procedenciaFondos" 
                                    control={control} 
                                    error={errors.procedenciaFondos}
                                    clearable={false}
                                    options={recaudadores.map(r => ({ value: r.rut, label: r.nombre }))}
                                    readonly
                                />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputText 
                                    label="Sucursal pago" 
                                    name="sucursalPago" 
                                    control={control} 
                                    error={errors.sucursalPago} 
                                    readonly/>
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputFecha 
                                    label="Fecha pago" 
                                    name="fechaPago" 
                                    fecha={watch().fechaPago} 
                                    control={control} 
                                    error={errors.fechaPago} 
                                    esReadOnly
                                />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputFecha 
                                    label="Fecha vencimiento" 
                                    name="fechaVencimiento" 
                                    fecha={watch().fechaVencimiento} 
                                    control={control} 
                                    error={errors.fechaVencimiento} 
                                    esReadOnly
                                />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputNumber
                                    label='Monto dividendo'
                                    name="montoDividendo"
                                    control={control}
                                    error={errors.montoDividendo}
                                    prefix='$'
                                    readonly
                                />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputNumber 
                                    label="Interés mora" 
                                    name="interesMora" 
                                    control={control} 
                                    error={errors.interesMora}
                                    prefix='$' 
                                    readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputNumber 
                                    label="Gastos cobranza" 
                                    name="gastosCobranza" 
                                    control={control} 
                                    error={errors.gastosCobranza}
                                    prefix='$' 
                                    readonly/>
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputNumber 
                                    label="Total recaudado" 
                                    name="totalRecaudado" 
                                    control={control} 
                                    error={errors.totalRecaudado}
                                    prefix='$' 
                                    readonly />
                            </Grid2>
                            <Grid2 size={{xs:12, sm:6, md:4}}>   
                                <InputSelect 
                                    label="Forma pago" 
                                    name="formaPago" 
                                    control={control} 
                                    error={errors.formaPago}
                                    clearable={false}
                                    options={formasPago.map(r => ({ value: r.value, label: r.label }))}
                                    readonly
                                />
                            </Grid2>
                        </Grid2>
                    </form>
                </DialogContent>

                <DialogActions>
                    <MButton
                        variant="contained"
                        color="primary"
                        onClick={handleAceptar}
                        startIcon={<CheckCircleTwoToneIcon />}
                    >
                        Aceptar
                    </MButton>
                    <MButton
                        variant="contained"
                        color="inherit"
                        onClick={() => {
                            props.onHide();
                            reset();
                        }}
                        startIcon={<CloseIcon />}
                    >
                        Cancelar
                    </MButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ModalDeshacerRecaudacion


function obtenerMensajeConfirmacion(fechaRemesa: Date | null, fuerecaudadoporinversionista: boolean): string {

    if (!fechaRemesa) {
        return fuerecaudadoporinversionista 
            ? '¿Desea confirmar la reversión de la recaudación de este dividendo? Esta operación afectará a un dividendo recaudado por inversionista.'
            : '¿Desea confirmar la reversión de la recaudación de este dividendo? Esta acción restaurará el dividendo a su estado anterior.';
    }
    else {
        return 'Atención: Este dividendo ya ha sido remesado. ¿Desea proceder con la reversión de la recaudación?.'
    }

    
}


